import { useId, type ReactElement, type ReactNode } from 'react';
import { Link as AriaLink } from 'react-aria-components';
import { ButtonLink } from './Button';
import { cx } from './cx';
import { Icon, type IconName } from './Icon';
import s from './Signpost.module.css';

export interface SignpostAction {
  label: string;
  href: string;
  /** A small text link at the end of the line instead of a navy button under the words. */
  quiet?: boolean;
}

export interface SignpostProps {
  /** A bold line on top ("Next: the directory"), as a heading at `headingLevel`. */
  title?: string;
  /** Heading level for the title in the page outline. Default 2. */
  headingLevel?: 2 | 3;
  /** A round petrol mark before the words. */
  icon?: IconName;
  children: ReactNode;
  /** One way on: a navy button under the words, or a quiet link at the end of the line. */
  action?: SignpostAction;
  className?: string;
}

/**
 * A tinted callout that points somewhere: the next step in a setup ("Next: the directory", SYS2
 * and SYS3) or how something works here ("When you change a setting for someone…", SYS1). Not a
 * card: it holds a sentence or two and at most one way on, so it has no "?" of its own.
 */
export function Signpost({
  title,
  headingLevel = 2,
  icon,
  children,
  action,
  className,
}: SignpostProps): ReactElement {
  const headingId = useId();
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const button = action !== undefined && action.quiet !== true;
  return (
    <section
      className={cx(s.signpost, className)}
      {...(title === undefined ? {} : { 'aria-labelledby': headingId })}
    >
      {icon !== undefined && (
        <span className={s.mark} aria-hidden="true">
          <Icon name={icon} size={18} />
        </span>
      )}
      <div className={s.words}>
        {title !== undefined && (
          <Heading id={headingId} className={s.title}>
            {title}
          </Heading>
        )}
        <div className={s.text}>{children}</div>
        {button && (
          <ButtonLink href={action.href} variant="navy" size="sm" className={s.button}>
            {action.label}
          </ButtonLink>
        )}
      </div>
      {action !== undefined && action.quiet === true && (
        <AriaLink href={action.href} className={s.quiet}>
          {action.label}
        </AriaLink>
      )}
    </section>
  );
}
